/* WebNest — react-hot-toast renderer styled to match the redesign prototype's NSToast.
   We keep react-hot-toast as the engine (all existing toast.success/error/loading
   calls work unchanged) but render each toast with the prototype's look: tone bar,
   icon-tile, title, dismiss button and an auto-dismiss progress bar that pauses on
   hover. Leans on design tokens already in index.css. */
import React, { useState, useEffect } from 'react'
import { Icon } from './ns-icons'

/* react-hot-toast type → prototype tone */
const TONE = {
  success: { color: 'var(--success)', tile: 'success', icon: 'check' },
  error:   { color: 'var(--danger)',  tile: 'danger',  icon: 'x' },
  loading: { color: 'var(--accent)',  tile: '',        icon: 'bolt' },
  blank:   { color: 'var(--accent)',  tile: '',        icon: 'bell' },
}

function injectStyles() {
  if (typeof document === 'undefined' || document.getElementById('ns-toast-style')) return
  const el = document.createElement('style')
  el.id = 'ns-toast-style'
  el.textContent = `
    @keyframes nsToastIn  { from { opacity: 0; transform: translateY(16px) scale(0.97); }
                            to   { opacity: 1; transform: translateY(0) scale(1); } }
    @keyframes nsToastOut { from { opacity: 1; transform: translateY(0) scale(1); }
                            to   { opacity: 0; transform: translateY(12px) scale(0.97); } }
    @keyframes nsToastBar { from { transform: scaleX(1); } to { transform: scaleX(0); } }`
  document.head.appendChild(el)
}

export function NSHotToast({ t }) {
  const [paused, setPaused] = useState(false)
  useEffect(injectStyles, [])

  const tone = TONE[t.type] || TONE.blank
  const isLoading = t.type === 'loading'
  const msg = typeof t.message === 'function' ? t.message(t) : t.message

  return (
    <div
      {...t.ariaProps}
      role="status"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      style={{
        pointerEvents: 'auto', position: 'relative', overflow: 'hidden',
        display: 'flex', alignItems: 'center', gap: 9, padding: '8px 13px 8px 12px',
        maxWidth: 'min(340px, calc(100vw - 32px))',
        background: 'var(--bg-1)', border: '1px solid var(--border-strong)',
        borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-lg)',
        animation: t.visible
          ? 'nsToastIn 0.34s cubic-bezier(0.22,1,0.36,1) both'
          : 'nsToastOut 0.22s ease forwards',
      }}
    >
      {/* left tone bar */}
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: tone.color }} />

      <span className={`icon-tile ${tone.tile}`} style={{ width: 24, height: 24, fontSize: 13, flexShrink: 0 }}>
        {isLoading
          ? <span style={{ width: 12, height: 12, border: '2px solid var(--border-strong)', borderTopColor: tone.color, borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
          : <Icon name={tone.icon} />}
      </span>

      <div style={{ minWidth: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.005em', lineHeight: 1.4, wordBreak: 'break-word' }}>{msg}</div>

      {!isLoading && t.duration !== Infinity && (
        <span
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0, height: 2,
            transformOrigin: 'left center', background: tone.color, opacity: 0.5,
            animation: `nsToastBar linear forwards`,
            animationDuration: `${t.duration || 4000}ms`,
            animationPlayState: paused ? 'paused' : 'running',
          }}
        />
      )}
    </div>
  )
}
